import type { PageHelp } from '../../help/types';

/** Guide for `/projects/:code/releases` — publishing, verifying, history and rollback. */
export const releasesHelp: PageHelp = {
  title: 'Releases',
  intro:
    'Publishing, and the record of what went out. A release is a set of stored bytes, not a query — that is what makes verifying and rolling back possible.',
  sections: ['Choosing a target', 'Reading the history', 'Publish dialog', 'Release files'],
  contexts: {
    'verify-result': 'Click “Verify” at the top right.',
    'publish-dialog': 'Click “Publish” at the top right.',
    'files-dialog': 'Click “Files” on a row of the history.',
  },
  tips: [
    'A rollback stays live on a target with auto-publish switched on: auto-publish only publishes again once the draft has changed after the rollback, and then the draft goes out as usual.',
    'A publish that would upload exactly the same files as the last release is refused, so pressing “Publish” twice never creates two identical versions.',
  ],
  steps: [
    // ── Choosing a target ───────────────────────────────────────────────────────
    {
      selector: '[data-help="releases-header"]',
      title: 'See where the files go',
      body: 'The line under the title shows the selected target and the URL its files are published to — the folder the host application reads. Targets are set up in the project settings; without one, nothing can be published.',
      section: 'Choosing a target',
      i18n: {
        'de-AT': {
          title: 'Sehen, wohin die Dateien gehen',
          body: 'Die Zeile unter dem Titel zeigt das gewählte Ziel und die URL, unter der seine Dateien veröffentlicht werden — den Ordner, den die Host-Anwendung liest. Ziele werden in den Projekteinstellungen angelegt; ohne Ziel kann nichts veröffentlicht werden.',
        },
        'sr-Latn-RS': {
          title: 'Pregled odredišta datoteka',
          body: 'Red ispod naslova prikazuje izabrani cilj i URL na koji se njegove datoteke objavljuju — folder koji aplikacija domaćin čita. Ciljevi se podešavaju u podešavanjima projekta; bez cilja ništa se ne može objaviti.',
        },
      },
    },
    {
      selector: '[data-help="releases-target"]',
      title: 'Pick a target',
      body: 'A target is one destination, such as `dev` or `prod`. The history, “Verify” and “Publish” all work on the target chosen here; the first one by code is selected when the page opens.',
      section: 'Choosing a target',
      i18n: {
        'de-AT': {
          title: 'Ein Ziel wählen',
          body: 'Ein Ziel ist ein Speicherort, etwa `dev` oder `prod`. Der Verlauf, „Verify“ und „Publish“ beziehen sich alle auf das hier gewählte Ziel; beim Öffnen der Seite ist das erste nach Code ausgewählt.',
        },
        'sr-Latn-RS': {
          title: 'Izbor cilja',
          body: 'Cilj je jedno odredište, na primer `dev` ili `prod`. Istorija, „Verify“ i „Publish“ rade nad ciljem koji je ovde izabran; kada se stranica otvori, izabran je prvi cilj po kodu.',
        },
      },
    },
    {
      selector: '[data-help="releases-verify"]',
      title: 'Check what is live',
      body: 'Compares the files that are actually in blob storage with the hashes recorded by the target’s last published release. Use it when an application shows text you did not expect. It is greyed out for viewers — owners, translators, developers and administrators may run it — and it fails on a target that was never published.',
      section: 'Choosing a target',
      i18n: {
        'de-AT': {
          title: 'Prüfen, was live ist',
          body: 'Vergleicht die Dateien, die tatsächlich im Blob-Storage liegen, mit den Hashes, die das letzte veröffentlichte Release dieses Ziels gespeichert hat. Nutzen Sie das, wenn eine Anwendung unerwartete Texte zeigt. Für Viewer ist die Schaltfläche ausgegraut — Owner, Translator, Developer und Administratoren dürfen prüfen —, und bei einem nie veröffentlichten Ziel schlägt die Prüfung fehl.',
        },
        'sr-Latn-RS': {
          title: 'Provera onoga što je objavljeno',
          body: 'Poredi datoteke koje se zaista nalaze u blob storage-u sa hash vrednostima koje je zabeležilo poslednje objavljeno izdanje cilja. Koristite ovo kada aplikacija prikazuje tekst koji ne očekujete. Za posmatrače (viewer) dugme je sivo — proveru smeju da pokrenu vlasnici (owner), prevodioci (translator), developeri i administratori — a za cilj koji nikada nije objavljen provera ne uspeva.',
        },
      },
    },
    {
      selector: '[data-help="releases-verify-result"]',
      title: 'Read the verify result',
      body: 'Green means every live file matches the last release. Amber lists the locales whose live file differs, usually because a blob was edited or deleted by hand. Publishing does not repair that while the draft is unchanged — such a publish is refused — so the repair is “Restore”. The message goes away with its × or when you pick another target.',
      section: 'Choosing a target',
      context: 'verify-result',
      i18n: {
        'de-AT': {
          title: 'Das Prüfergebnis lesen',
          body: 'Grün bedeutet, dass jede Live-Datei dem letzten Release entspricht. Gelb nennt die Sprachen, deren Live-Datei abweicht — meist, weil ein Blob von Hand geändert oder gelöscht wurde. Ein Publish repariert das nicht, solange der Entwurf unverändert ist — ein solcher Publish wird abgelehnt —, die Reparatur ist daher „Restore“. Die Meldung verschwindet mit ihrem × oder wenn Sie ein anderes Ziel wählen.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje rezultata provere',
          body: 'Zeleno znači da se svaka objavljena datoteka poklapa sa poslednjim izdanjem. Žuto navodi jezike čija se objavljena datoteka razlikuje, obično zato što je blob ručno izmenjen ili obrisan. Objavljivanje to ne popravlja dok je nacrt nepromenjen — takvo objavljivanje se odbija — pa je popravka „Restore“. Poruka nestaje klikom na × ili kada izaberete drugi cilj.',
        },
      },
    },
    {
      selector: '[data-help="releases-restore-drift"]',
      title: 'Restore the live files',
      body: 'Rolls back to the very version that Verify checked, after you confirm: the bytes that release recorded are uploaded again as a new version, so the live files match once more. Only the project owner and administrators see the button; anyone else reads which version to roll back to in the message.',
      section: 'Choosing a target',
      context: 'verify-result',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Live-Dateien wiederherstellen',
          body: 'Setzt nach Ihrer Bestätigung genau auf die Version zurück, die „Verify“ geprüft hat: Die gespeicherten Bytes dieses Releases werden als neue Version erneut hochgeladen, und die Live-Dateien stimmen wieder. Nur der Projekt-Owner und Administratoren sehen die Schaltfläche; alle anderen lesen in der Meldung, auf welche Version zurückgesetzt werden muss.',
        },
        'sr-Latn-RS': {
          title: 'Vraćanje objavljenih datoteka',
          body: 'Nakon potvrde vraća upravo na verziju koju je „Verify“ proverio: sačuvani bajtovi tog izdanja ponovo se otpremaju kao nova verzija, pa se objavljene datoteke opet poklapaju. Dugme vide samo vlasnik projekta (owner) i administratori; svi ostali u poruci čitaju na koju verziju treba vratiti.',
        },
      },
    },
    {
      selector: '[data-help="releases-publish"]',
      title: 'Publish the current draft',
      body: 'Opens the publish dialog with the changes since the last release. Owners, translators, developers and administrators may publish; the target `prod` is limited to owners and administrators, and to translators when the project settings allow it. The button is greyed out for anyone who may not publish the selected target.',
      section: 'Choosing a target',
      i18n: {
        'de-AT': {
          title: 'Den aktuellen Entwurf veröffentlichen',
          body: 'Öffnet den Publish-Dialog mit den Änderungen seit dem letzten Release. Owner, Translator und Developer dürfen veröffentlichen; das Ziel `prod` ist Ownern und Administratoren vorbehalten, sowie Translatorn, wenn die Projekteinstellungen es erlauben. Wer das gewählte Ziel nicht veröffentlichen darf, sieht die Schaltfläche ausgegraut.',
        },
        'sr-Latn-RS': {
          title: 'Objavljivanje trenutnog nacrta',
          body: 'Otvara dijalog za objavljivanje sa izmenama od poslednjeg izdanja. Vlasnici (owner), prevodioci (translator) i developeri smeju da objavljuju; cilj `prod` je ograničen na vlasnike i administratore, kao i na prevodioce kada to dozvoljavaju podešavanja projekta. Ko ne sme da objavi izabrani cilj, vidi sivo dugme.',
        },
      },
    },

    // ── Reading the history ─────────────────────────────────────────────────────
    {
      selector: '[data-help="releases-history"]',
      title: 'Read the release history',
      body: 'Every release of the selected target, newest first, with its version, the number of keys, when it started and who started it. The first publish creates version 1, and a release is never changed afterwards.',
      section: 'Reading the history',
      i18n: {
        'de-AT': {
          title: 'Den Release-Verlauf lesen',
          body: 'Alle Releases des gewählten Ziels, das neueste zuerst, mit Version, Anzahl der Keys, Startzeit und wer es gestartet hat. Der erste Publish erzeugt Version 1, und ein Release wird danach nie mehr verändert.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje istorije izdanja',
          body: 'Sva izdanja izabranog cilja, od najnovijeg, sa verzijom, brojem ključeva, vremenom početka i osobom koja ga je pokrenula. Prvo objavljivanje pravi verziju 1, a izdanje se nakon toga nikada ne menja.',
        },
      },
    },
    {
      selector: '[data-help="releases-status"]',
      title: 'Read the release status',
      body: '`published` means every file was uploaded and copied to its final path. `failed` means the upload did not finish and applications still read the previous release; open “Files” to read the error. `pending` and `uploading` belong to a release that is still in progress.',
      section: 'Reading the history',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Den Release-Status lesen',
          body: '`published` bedeutet, dass jede Datei hochgeladen und an ihren endgültigen Ort kopiert wurde. `failed` bedeutet, dass der Upload nicht fertig wurde und Anwendungen weiterhin das vorige Release lesen; den Fehler finden Sie unter „Files“. `pending` und `uploading` gehören zu einem Release, das noch läuft.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje statusa izdanja',
          body: '`published` znači da je svaka datoteka otpremljena i kopirana na konačnu putanju. `failed` znači da otpremanje nije završeno i da aplikacije i dalje čitaju prethodno izdanje; grešku pogledajte pod „Files“. `pending` i `uploading` označavaju izdanje koje je još u toku.',
        },
      },
    },
    {
      selector: '[data-help="releases-trigger"]',
      title: 'See what started a release',
      body: '`manual` was started by a person, `auto` by auto-publish, `api` by a pipeline with an API token, and `rollback` re-uploaded an earlier release — followed by “(of version …)” naming the version it restored.',
      section: 'Reading the history',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Sehen, was ein Release ausgelöst hat',
          body: '`manual` wurde von einer Person gestartet, `auto` von Auto-Publish, `api` von einer Pipeline mit API-Token, und `rollback` hat ein früheres Release erneut hochgeladen — danach steht „(of version …)“ mit der wiederhergestellten Version.',
        },
        'sr-Latn-RS': {
          title: 'Pregled onoga što je pokrenulo izdanje',
          body: '`manual` je pokrenula osoba, `auto` automatsko objavljivanje, `api` pipeline sa API tokenom, a `rollback` je ponovo otpremio ranije izdanje — iza toga stoji „(of version …)“ sa verzijom koja je vraćena.',
        },
      },
    },
    {
      selector: '[data-help="releases-files"]',
      title: 'Open the files of a release',
      body: '“Files” shows what the release wrote, one file per locale, with key counts, sizes and hashes, compared with the release before it. For a failed release it also shows the error.',
      section: 'Reading the history',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Dateien eines Releases öffnen',
          body: '„Files“ zeigt, was das Release geschrieben hat — eine Datei pro Sprache, mit Anzahl der Keys, Größe und Hash, verglichen mit dem Release davor. Bei einem fehlgeschlagenen Release steht dort auch der Fehler.',
        },
        'sr-Latn-RS': {
          title: 'Otvaranje datoteka izdanja',
          body: '„Files“ prikazuje šta je izdanje upisalo — jednu datoteku po jeziku, sa brojem ključeva, veličinom i hash vrednošću, u poređenju sa prethodnim izdanjem. Za neuspelo izdanje tu je prikazana i greška.',
        },
      },
    },
    {
      selector: '[data-help="releases-rollback"]',
      title: 'Roll back to an earlier release',
      body: 'After you confirm, the stored bytes of that release are uploaded again as a new version. Nothing is deleted and the draft is untouched, so it is safe to use in a hurry; auto-publish leaves the rollback live until the draft changes again. The button is greyed out on releases that were not published, and for everyone except the project owner and administrators.',
      section: 'Reading the history',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Auf ein früheres Release zurücksetzen',
          body: 'Nach Ihrer Bestätigung werden die gespeicherten Bytes dieses Releases als neue Version erneut hochgeladen. Nichts wird gelöscht und der Entwurf bleibt unverändert — das ist also auch in Eile sicher; Auto-Publish lässt den Rollback live, bis sich der Entwurf wieder ändert. Die Schaltfläche ist bei nicht veröffentlichten Releases ausgegraut, und für alle außer dem Projekt-Owner und Administratoren.',
        },
        'sr-Latn-RS': {
          title: 'Vraćanje na ranije izdanje',
          body: 'Nakon potvrde, sačuvani bajtovi tog izdanja ponovo se otpremaju kao nova verzija. Ništa se ne briše i nacrt ostaje netaknut, pa je ovo bezbedno i kada se žuri; automatsko objavljivanje ostavlja vraćeno izdanje aktivnim dok se nacrt ponovo ne promeni. Dugme je sivo za izdanja koja nisu objavljena, kao i za sve osim vlasnika projekta (owner) i administratora.',
        },
      },
    },

    // ── Publish dialog ──────────────────────────────────────────────────────────
    {
      selector: '[data-help="releases-publish-gate"]',
      title: 'Know the reviewed gate',
      body: 'Shown when the target only publishes reviewed translations. The publish is then refused while any value in the locales being published is still draft or machine.',
      section: 'Publish dialog',
      context: 'publish-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Die Sperre für geprüfte Übersetzungen kennen',
          body: 'Wird angezeigt, wenn das Ziel nur geprüfte Übersetzungen veröffentlicht. Der Publish wird dann abgelehnt, solange ein Wert in den zu veröffentlichenden Sprachen noch draft oder machine ist.',
        },
        'sr-Latn-RS': {
          title: 'Uslov za pregledane prevode',
          body: 'Prikazuje se kada cilj objavljuje samo pregledane prevode. Objavljivanje se tada odbija dok je bilo koja vrednost na jezicima koji se objavljuju još u statusu draft ili machine.',
        },
      },
    },
    {
      selector: '[data-help="releases-publish-blocked"]',
      title: 'Understand a refused publish',
      body: 'Appears when the server refuses, with its reason. “Nothing has changed since the last release” means the files would be byte-identical, so no version is created. A gate refusal also counts the values in the way — “at least 50” when there are more than the server reports: fix them in the editor, or ask an administrator, the only role allowed to force a publish past the gates. The message is cleared when you close the dialog.',
      section: 'Publish dialog',
      context: 'publish-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Einen abgelehnten Publish verstehen',
          body: 'Erscheint, wenn der Server ablehnt, mit seiner Begründung. „Nothing has changed since the last release“ bedeutet, dass die Dateien byte-gleich wären — es entsteht also keine Version. Eine Ablehnung durch eine Sperre zählt zusätzlich die Werte, die im Weg sind — „at least 50“, wenn es mehr sind, als der Server meldet: Beheben Sie sie im Editor oder wenden Sie sich an einen Administrator, die einzige Rolle, die einen Publish an den Sperren vorbei erzwingen darf. Beim Schließen des Dialogs verschwindet die Meldung.',
        },
        'sr-Latn-RS': {
          title: 'Razumevanje odbijenog objavljivanja',
          body: 'Pojavljuje se kada server odbije, sa njegovim obrazloženjem. „Nothing has changed since the last release“ znači da bi datoteke bile identične do poslednjeg bajta, pa se verzija ne pravi. Odbijanje zbog uslova dodatno broji vrednosti koje smetaju — „at least 50“ kada ih ima više nego što server prijavljuje: ispravite ih u editoru ili se obratite administratoru, jedinoj ulozi koja sme da nametne objavljivanje mimo uslova. Poruka nestaje kada zatvorite dijalog.',
        },
      },
    },
    {
      selector: '[data-help="releases-publish-note"]',
      title: 'Add a note',
      body: 'Optional: one line about what is going out, up to 1000 characters. It is stored with the release.',
      section: 'Publish dialog',
      context: 'publish-dialog',
      i18n: {
        'de-AT': {
          title: 'Eine Notiz hinzufügen',
          body: 'Optional: eine Zeile dazu, was veröffentlicht wird, höchstens 1000 Zeichen. Sie wird mit dem Release gespeichert.',
        },
        'sr-Latn-RS': {
          title: 'Dodavanje beleške',
          body: 'Opciono: jedan red o tome šta se objavljuje, najviše 1000 znakova. Čuva se uz izdanje.',
        },
      },
    },
    {
      selector: '[data-help="releases-publish-locales"]',
      title: 'Publish only some locales',
      body: 'Every locale is ticked, which publishes them all. Untick the ones that are not ready and only the ticked locales are built and uploaded; the others stay exactly as they are live. The new release still lists every locale — the ones left out as “carried over” from the previous release — so a rollback to it restores the whole set. Auto-publish does not count such a release as up to date and sends the remaining locales out on its next run.',
      section: 'Publish dialog',
      context: 'publish-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Nur einige Sprachen veröffentlichen',
          body: 'Alle Sprachen sind angehakt, dann werden alle veröffentlicht. Nehmen Sie den Haken bei denen weg, die noch nicht fertig sind: Nur die angehakten Sprachen werden erzeugt und hochgeladen, die anderen bleiben genau so, wie sie live sind. Das neue Release führt trotzdem alle Sprachen — die ausgelassenen als „carried over“ aus dem vorigen Release —, ein Rollback darauf stellt also den ganzen Satz wieder her. Auto-Publish betrachtet ein solches Release nicht als aktuell und veröffentlicht die übrigen Sprachen beim nächsten Durchlauf.',
        },
        'sr-Latn-RS': {
          title: 'Objavljivanje samo nekih jezika',
          body: 'Svi jezici su označeni, pa se objavljuju svi. Skinite oznaku sa onih koji nisu spremni: prave se i otpremaju samo označeni jezici, a ostali ostaju tačno onakvi kakvi su objavljeni. Novo izdanje i dalje sadrži sve jezike — izostavljene kao „carried over“ iz prethodnog izdanja — pa vraćanje na njega vraća ceo skup. Automatsko objavljivanje takvo izdanje ne smatra ažurnim i preostale jezike objavljuje u sledećem prolazu.',
        },
      },
    },
    {
      selector: '[data-help="releases-publish-diff-locale"]',
      title: 'Choose the locale to compare',
      body: 'The diff is shown one locale at a time, starting with the source locale. Switching it only changes what you see: “Publish” uploads the locales ticked above.',
      section: 'Publish dialog',
      context: 'publish-dialog',
      i18n: {
        'de-AT': {
          title: 'Die Vergleichssprache wählen',
          body: 'Der Vergleich wird jeweils für eine Sprache gezeigt, zuerst für die Ausgangssprache. Ein Wechsel ändert nur die Anzeige: „Publish“ lädt die oben angehakten Sprachen hoch.',
        },
        'sr-Latn-RS': {
          title: 'Izbor jezika za poređenje',
          body: 'Razlike se prikazuju za jedan jezik, počevši od izvornog. Promena jezika menja samo prikaz: „Publish“ otprema jezike označene iznad.',
        },
      },
    },
    {
      selector: '[data-help="releases-publish-diff"]',
      title: 'Read what changes',
      body: 'Compares the current draft with the target’s last published release, freshly every time the dialog opens: added, changed (old value struck through, new value next to it) and removed keys, at most 100 per group. On a target that was never published everything is new. Untranslated keys are left out of the files, so a value cleared in the editor shows up as removed.',
      section: 'Publish dialog',
      context: 'publish-dialog',
      i18n: {
        'de-AT': {
          title: 'Lesen, was sich ändert',
          body: 'Vergleicht den aktuellen Entwurf mit dem letzten veröffentlichten Release des Ziels, bei jedem Öffnen des Dialogs neu: hinzugefügte, geänderte (alter Wert durchgestrichen, neuer daneben) und entfernte Keys, höchstens 100 pro Gruppe. Bei einem nie veröffentlichten Ziel ist alles neu. Unübersetzte Keys fehlen in den Dateien, ein im Editor geleerter Wert erscheint daher als entfernt.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje izmena',
          body: 'Poredi trenutni nacrt sa poslednjim objavljenim izdanjem cilja, iznova pri svakom otvaranju dijaloga: dodati, izmenjeni (stara vrednost precrtana, nova pored nje) i uklonjeni ključevi, najviše 100 po grupi. Za cilj koji nikada nije objavljen sve je novo. Neprevedeni ključevi se izostavljaju iz datoteka, pa se vrednost obrisana u editoru prikazuje kao uklonjena.',
        },
      },
    },
    {
      selector: '[data-help="releases-publish-confirm"]',
      title: 'Confirm the publish',
      body: 'Builds the files for the ticked locales, uploads them to a staging folder and only then copies them to the live paths, so an application never sees half of a new set. When some locales are left out, the button says how many are published. The new version appears in the history.',
      section: 'Publish dialog',
      context: 'publish-dialog',
      i18n: {
        'de-AT': {
          title: 'Den Publish bestätigen',
          body: 'Erzeugt die Dateien für die angehakten Sprachen, lädt sie in einen Staging-Ordner hoch und kopiert sie erst danach an die Live-Pfade — eine Anwendung sieht also nie einen halb erneuerten Satz. Sind Sprachen ausgelassen, nennt die Schaltfläche, wie viele veröffentlicht werden. Die neue Version erscheint im Verlauf.',
        },
        'sr-Latn-RS': {
          title: 'Potvrda objavljivanja',
          body: 'Pravi datoteke za označene jezike, otprema ih u staging folder i tek onda ih kopira na objavljene putanje, pa aplikacija nikada ne vidi napola zamenjen skup. Kada su neki jezici izostavljeni, dugme kaže koliko ih se objavljuje. Nova verzija se pojavljuje u istoriji.',
        },
      },
    },

    // ── Release files ───────────────────────────────────────────────────────────
    {
      selector: '[data-help="releases-files-error"]',
      title: 'Read why a release failed',
      body: 'A failed release shows the error the upload stopped with. Until a later publish succeeds, applications keep reading the previous release.',
      section: 'Release files',
      context: 'files-dialog',
      optional: true,
      i18n: {
        'de-AT': {
          title: 'Lesen, warum ein Release fehlgeschlagen ist',
          body: 'Ein fehlgeschlagenes Release zeigt den Fehler, mit dem der Upload abgebrochen ist. Bis ein späterer Publish gelingt, lesen Anwendungen weiterhin das vorige Release.',
        },
        'sr-Latn-RS': {
          title: 'Razlog neuspelog izdanja',
          body: 'Neuspelo izdanje prikazuje grešku na kojoj se otpremanje zaustavilo. Dok neko kasnije objavljivanje ne uspe, aplikacije i dalje čitaju prethodno izdanje.',
        },
      },
    },
    {
      selector: '[data-help="releases-files-table"]',
      title: 'Read the files of a release',
      body: 'One row per locale with the number of keys (the change against the previous release in brackets), the file size, whether the file changed, and the start of its sha256 hash. The hash is what “Verify” compares the live file with. “carried over” marks a locale a partial publish left out: the release lists the bytes of the previous one for it.',
      section: 'Release files',
      context: 'files-dialog',
      i18n: {
        'de-AT': {
          title: 'Die Dateien eines Releases lesen',
          body: 'Eine Zeile pro Sprache mit der Anzahl der Keys (in Klammern die Veränderung gegenüber dem vorigen Release), der Dateigröße, ob sich die Datei geändert hat, und dem Anfang ihres sha256-Hashes. Mit diesem Hash vergleicht „Verify“ die Live-Datei. „carried over“ kennzeichnet eine Sprache, die ein teilweiser Publish ausgelassen hat: Das Release führt für sie die Bytes des vorigen.',
        },
        'sr-Latn-RS': {
          title: 'Čitanje datoteka izdanja',
          body: 'Jedan red po jeziku, sa brojem ključeva (u zagradi promena u odnosu na prethodno izdanje), veličinom datoteke, oznakom da li se datoteka promenila i početkom njenog sha256 hash-a. Sa tim hash-om „Verify“ poredi objavljenu datoteku. „carried over“ označava jezik koji je delimično objavljivanje izostavilo: izdanje za njega navodi bajtove prethodnog.',
        },
      },
    },
  ],
  i18n: {
    'de-AT': {
      title: 'Releases',
      intro:
        'Veröffentlichen, und die Aufzeichnung dessen, was hinausgegangen ist. Ein Release ist ein Satz gespeicherter Bytes, keine Abfrage — das macht Prüfen und Zurücksetzen erst möglich.',
      sections: {
        'Choosing a target': 'Ziel wählen',
        'Reading the history': 'Den Verlauf lesen',
        'Publish dialog': 'Publish-Dialog',
        'Release files': 'Dateien eines Releases',
      },
      contexts: {
        'verify-result': 'Klicken Sie rechts oben auf „Verify“.',
        'publish-dialog': 'Klicken Sie rechts oben auf „Publish“.',
        'files-dialog': 'Klicken Sie in einer Zeile des Verlaufs auf „Files“.',
      },
      tips: [
        'Ein Rollback bleibt auch bei einem Ziel mit eingeschaltetem Auto-Publish live: Auto-Publish veröffentlicht erst wieder, wenn sich der Entwurf nach dem Rollback geändert hat — dann geht der Entwurf wie gewohnt hinaus.',
        'Ein Publish, der genau dieselben Dateien wie das letzte Release hochladen würde, wird abgelehnt — zweimal „Publish“ zu drücken erzeugt also nie zwei identische Versionen.',
      ],
    },
    'sr-Latn-RS': {
      title: 'Izdanja',
      intro:
        'Objavljivanje i evidencija onoga što je objavljeno. Izdanje je skup sačuvanih bajtova, a ne upit — upravo to omogućava proveru i vraćanje na ranije izdanje.',
      sections: {
        'Choosing a target': 'Izbor cilja',
        'Reading the history': 'Čitanje istorije',
        'Publish dialog': 'Dijalog za objavljivanje',
        'Release files': 'Datoteke izdanja',
      },
      contexts: {
        'verify-result': 'Kliknite na „Verify“ u gornjem desnom uglu.',
        'publish-dialog': 'Kliknite na „Publish“ u gornjem desnom uglu.',
        'files-dialog': 'Kliknite na „Files“ u nekom redu istorije.',
      },
      tips: [
        'Vraćanje na ranije izdanje ostaje aktivno i na cilju sa uključenim automatskim objavljivanjem: automatsko objavljivanje ponovo objavljuje tek kada se nacrt promeni posle vraćanja, i tada nacrt izlazi kao i obično.',
        'Objavljivanje koje bi otpremilo potpuno iste datoteke kao poslednje izdanje se odbija, pa dvostruki klik na „Publish“ nikada ne pravi dve identične verzije.',
      ],
    },
  },
};
